import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpClient, HttpHeaders, HttpClientModule } from "@angular/common/http";
import { Router } from "@angular/router";

import { DialogModule } from "primeng/dialog";
import { AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { ConfirmationService } from 'primeng/api';
import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { ClinicScheduleService } from '../../../service/admin/clinic-schedule.service';
import { UserService } from '../../../service/admin/user.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';
import { ScrollPanelModule } from "primeng/scrollpanel";
import { ApiService } from '../../../service/admin/api.service';
import { WorkContextService } from '../../../service/admin/work-context.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { shiftDateString, todayDateString } from '../../../utils/clinic-date.util';
import { DELIVERY_TYPE_OPTIONS, DeliveryType, deliveryTypeShortLabel, resolveDeliveryType } from '../../../utils/delivery-type.util';
import {
  MEDICINE_INTENT_OPTIONS,
  MedicineIntent,
  medicineIntentShortLabel,
  receptionNoteTooltip,
} from '../../../utils/medicine-intent.util';
import { ToasterService } from '../../../service/common/toaster.service';

@Component({
  selector: "app-calendar",
  standalone: true,
  imports: [
    CommonModule, FormsModule, HttpClientModule,
    DialogModule, AutoCompleteModule, ButtonModule, ToastModule, DropdownModule, ScrollPanelModule, FloatLabelModule,
    WorkContextBarComponent
  ],
  styleUrls: ['./calender.component.scss'],
  providers: [AppointmentService, ConfirmationService, ClinicService, ClinicScheduleService, UserService, PatientRegistrationService, ApiService],
  template: `
    <div class="calendar-shell" [class.is-embedded]="!showContextBar">
      <div class="calendar-toolbar">
        <div class="date-navigator">
          <button type="button" class="nav-btn" (click)="previousDay()">
            <i class="pi pi-chevron-left"></i>
          </button>
          <input type="date" class="date-input" [(ngModel)]="currentDateString" (change)="onDateChange()">
          <button type="button" class="nav-btn" (click)="nextDay()">
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>

        <div class="calendar-context-slot" *ngIf="showContextBar">
          <app-work-context-bar [compact]="true" />
        </div>
      </div>

      <div class="clinic-columns-container">
        <!-- Iterate over clinics -->
        <div class="clinic-column" *ngFor="let clinic of clinicList">
          <div class="clinic-header">
            <h5 class="clinic-title">{{ clinic.label }}</h5>
            <button class="add-btn" (click)="openAddAppointment(clinic.value)">
              <i class="pi pi-plus"></i> Add
            </button>
          </div>
          <div class="clinic-body">
            <ng-container *ngIf="groupedAppointments[clinic.value] && groupedAppointments[clinic.value].length > 0; else noAppts">
              <div class="appointment-card" 
                   *ngFor="let appt of groupedAppointments[clinic.value]"
                   [style.borderLeftColor]="getStatusColor(getAppointmentStatus(appt))">
                
                <div class="card-header">
                  <span class="patient-name">{{ appt.patient?.salutation }} {{ appt.patient?.full_name }}</span>
                  <span class="appt-time"><i class="pi pi-clock"></i> {{ formatTime(appt.start_time) }}</span>
                </div>
                
                <div class="card-body">
                  <div class="card-body-row">
                    <div class="doc-info"><i class="pi pi-user"></i> {{ appt.doctor?.first_name }} {{ appt.doctor?.last_name }}</div>
                    <div class="card-status-pills">
                      <div
                        *ngFor="let badge of getAppointmentStatusBadges(appt)"
                        class="status-badge"
                        [style.color]="badge.color"
                        [style.backgroundColor]="badge.bg"
                        [style.borderColor]="badge.border"
                      >
                        {{ badge.label }}
                      </div>
                    </div>
                  </div>
                  <div class="card-intake-note" *ngIf="appt.notes" [title]="getReceptionNoteTooltip(appt)">
                    <span
                      *ngIf="appt.medicine_intent"
                      class="medicine-intent-badge"
                      [ngClass]="'medicine-intent-badge--' + appt.medicine_intent"
                    >{{ getMedicineIntentShort(appt.medicine_intent) }}</span>
                    <i class="pi pi-whatsapp"></i>
                    <span class="card-intake-note-text">{{ appt.notes }}</span>
                  </div>
                  <div class="card-appointment-note" *ngIf="appt.appointment_note" [title]="appt.appointment_note">
                    <i class="pi pi-file-edit"></i>
                    <span class="card-appointment-note-text">{{ appt.appointment_note }}</span>
                  </div>
                </div>
                
                <div class="card-actions">
                  <button
                    type="button"
                    class="intake-note-btn"
                    (click)="openIntakeNoteEditor(appt)"
                  >
                    <i class="pi pi-whatsapp"></i>
                    {{ appt.notes ? 'Edit WhatsApp' : 'Add WhatsApp' }}
                  </button>
                  <button
                    type="button"
                    class="intake-note-btn intake-note-btn--doctor"
                    (click)="openAppointmentNoteEditor(appt)"
                  >
                    <i class="pi pi-file-edit"></i>
                    {{ appt.appointment_note ? 'Edit appt note' : 'Add appt note' }}
                  </button>
                  <button
                    *ngIf="appt.status === 'checked in' && userType !== 'reception'"
                    class="consult-btn"
                    (click)="goToConsultation(appt)"
                  >
                    <i class="pi pi-play" style="font-size: 0.8rem"></i> Start Consultation
                  </button>
                </div>
                
              </div>
            </ng-container>
            <ng-template #noAppts>
              <div class="empty-state">No appointments scheduled</div>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Patient Selection Dialog -->
    <p-dialog 
        header="New Appointment" 
        [(visible)]="patientDialogVisible" 
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{width: '70vw', maxWidth: '1450px'}"
        [breakpoints]="{'1200px':'95vw','768px':'100vw'}"
        [contentStyle]="{
          'max-height':'90vh',
          'overflow':'hidden',
          'padding':'0.75rem'
        }">

        <div class="container-fluid p-1 appointment-dialog-compact">

          <!-- TOP ROW: patient, clinic, doctor, date -->
          <div class="row g-2 align-items-end mb-2 appointment-top-row">

            <!-- Patient -->
            <div class="col-md-5">
              <small class="compact-label">
                Patient <span class="text-danger">*</span>
              </small>

              <p-autoComplete 
                inputId="patient"
                [(ngModel)]="selectedPatient" 
                [suggestions]="patients"
                (completeMethod)="searchPatients($event)" 
                (onSelect)="onPatientSelect($event)"
                field="full_name"
                placeholder="Search patient..."
                [forceSelection]="true"
                emptyMessage="No patients found"
                appendTo="body"
                styleClass="w-100 compact-input"
                [inputStyle]="{'width':'100%'}">

                <ng-template let-patient pTemplate="item">
                  <div class="d-flex align-items-center gap-2">
                    <i class="pi pi-user text-primary"></i>
                    <div>{{ patient.full_name }}</div>
                  </div>
                </ng-template>

              </p-autoComplete>
            </div>

            <!-- Clinic -->
            <div class="col-md-2">
              <small class="compact-label">
                Clinic <span class="text-danger">*</span>
              </small>

              <p-dropdown 
                id="clinic"
                [options]="clinicList" 
                [(ngModel)]="appointment.clinic_id" 
                optionLabel="label"
                optionValue="value"
                placeholder="Clinic"
                appendTo="body"
                (onChange)="onClinicChange()"
                styleClass="w-100 compact-input">
              </p-dropdown>
            </div>

            <!-- Doctor -->
            <div class="col-md-2">
              <small class="compact-label">
                Doctor <span class="text-danger">*</span>
              </small>

              <p-dropdown 
                id="doctor"
                [options]="doctorsList" 
                [(ngModel)]="appointment.doctor_id" 
                optionLabel="label"
                optionValue="value"
                placeholder="Doctor"
                appendTo="body"
                styleClass="w-100 compact-input">
              </p-dropdown>
            </div>

            <!-- Date -->
            <div class="col-md-3">
              <small class="compact-label">
                Appointment Date <span class="text-danger">*</span>
              </small>

              <div class="compact-date-wrapper">
                <span class="date-icon">
                  <i class="pi pi-calendar"></i>
                </span>

                <input 
                  type="date"
                  class="form-control compact-date-input"
                  id="appointment_date"
                  name="appointment_date"
                  (change)="generateTimeSlots()"
                  [(ngModel)]="appointment.appointment_date">
              </div>
            </div>

          </div>

          <!-- SECOND ROW: delivery + reception note -->
          <div class="appointment-second-row">

            <div class="appointment-second-row__delivery">
              <small class="compact-label mb-1">Delivery</small>
              <div class="appointment-flow-badge-group">
                <button
                  type="button"
                  class="appointment-flow-badge appointment-flow-badge--medicine"
                  [class.is-active]="appointment.medicine_collection"
                  (click)="setMedicineCollection(!appointment.medicine_collection)">
                  <i class="pi pi-box" aria-hidden="true"></i>
                  Medicine
                </button>
                <button
                  *ngFor="let option of deliveryTypeOptions"
                  type="button"
                  class="appointment-flow-badge"
                  [ngClass]="'appointment-flow-badge--' + option.value"
                  [class.is-active]="isDeliveryTypeActive(option.value)"
                  (click)="setDeliveryType(option.value)">
                  <i class="pi" [ngClass]="option.icon" aria-hidden="true"></i>
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div class="appointment-second-row__reception">
              <small class="compact-label">
                WhatsApp number
                <span class="text-muted fw-normal">(reception)</span>
              </small>
              <div class="reception-note-inline">
                <input
                  type="tel"
                  class="form-control compact-reception-input reception-note-input"
                  [(ngModel)]="appointment.notes"
                  (ngModelChange)="onReceptionNoteChange($event, 'appointment')"
                  maxlength="10"
                  size="10"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  placeholder="9876543210"
                />
                <div class="reception-intent-group">
                  <button
                    *ngFor="let option of medicineIntentOptions"
                    type="button"
                    class="medicine-intent-badge-btn"
                    [ngClass]="'medicine-intent-badge-btn--' + option.value"
                    [class.is-active]="isMedicineIntentActive(option.value, 'appointment')"
                    [title]="option.title"
                    (click)="setMedicineIntent(option.value, 'appointment')"
                  >
                    {{ option.shortLabel }}
                  </button>
                </div>
              </div>
            </div>

            <div class="appointment-second-row__doctor-note">
              <small class="compact-label">
                Appointment note
                <span class="text-muted fw-normal">(for doctor)</span>
              </small>
              <textarea
                class="form-control appointment-note-input"
                rows="2"
                [(ngModel)]="appointment.appointment_note"
                maxlength="2000"
                placeholder="Reason for visit, follow-up context, etc."
              ></textarea>
            </div>

          </div>

          <!-- TIME SLOTS -->
          <div class="mt-3">

            <div class="time-slot-container">

              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="pi pi-clock text-primary"></i>

                <small class="compact-label mb-0">
                  Select Time Slot <span class="text-danger">*</span>
                </small>
              </div>

              <div class="row g-2">

                <div class="col-12" *ngIf="timeSlots.length === 0">
                  <div class="slot-empty-note">
                    No appointment slots available for this clinic on the selected date.
                  </div>
                </div>

                <div 
                  class="col-3"
                  *ngFor="let slot of timeSlots">

                  <button
                    type="button"
                    class="slot-btn w-100"
                    [class.selected]="selectedSlot === slot"
                    [disabled]="slot.disabled"
                    (click)="selectSlot(slot)">

                    <i 
                      class="pi pi-check me-1"
                      *ngIf="selectedSlot === slot">
                    </i>

                    {{ slot.label }}

                  </button>

                </div>

              </div>

            </div>

          </div>

        </div>

        <!-- FOOTER -->
        <ng-template pTemplate="footer">

          <div class="d-flex justify-content-end gap-2 pt-2">

            <button 
              class="btn btn-sm btn-light border px-3"
              (click)="patientDialogVisible=false">

              Cancel

            </button>

            <button 
              class="btn btn-sm btn-primary px-3 d-flex align-items-center gap-2"
              [disabled]="!selectedPatient || !selectedSlot"
              (click)="confirmPatientSelection()">

              <i class="pi pi-check-circle"></i>

              Confirm Appointment

            </button>

          </div>

        </ng-template>

    </p-dialog>

    <p-dialog
      header="Reception note"
      [(visible)]="intakeNoteDialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '32rem', maxWidth: '95vw' }"
    >
      <p class="intake-note-dialog-hint mb-2">
        WhatsApp number for today's chat. Pick C (continue meds) or R (regular).
      </p>
      <div class="reception-note-inline reception-note-inline--dialog">
        <input
          type="tel"
          class="form-control compact-reception-input reception-note-input"
          [(ngModel)]="editingIntakeNote"
          (ngModelChange)="onReceptionNoteChange($event, 'edit')"
          maxlength="10"
          size="10"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="9876543210"
        />
        <div class="reception-intent-group">
          <button
            *ngFor="let option of medicineIntentOptions"
            type="button"
            class="medicine-intent-badge-btn"
            [ngClass]="'medicine-intent-badge-btn--' + option.value"
            [class.is-active]="isMedicineIntentActive(option.value, 'edit')"
            [title]="option.title"
            (click)="setMedicineIntent(option.value, 'edit')"
          >
            {{ option.shortLabel }}
          </button>
        </div>
      </div>
      <ng-template pTemplate="footer">
        <div class="d-flex justify-content-end gap-2 pt-2">
          <button class="btn btn-sm btn-light border px-3" (click)="intakeNoteDialogVisible = false">
            Cancel
          </button>
          <button class="btn btn-sm btn-primary px-3" (click)="saveIntakeNote()">
            Save note
          </button>
        </div>
      </ng-template>
    </p-dialog>

    <p-dialog
      header="Appointment note"
      [(visible)]="appointmentNoteDialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '34rem', maxWidth: '95vw' }"
    >
      <p class="intake-note-dialog-hint mb-2">
        This note will appear in the doctor's consultation notes when they start the visit.
      </p>
      <textarea
        class="form-control appointment-note-input"
        rows="4"
        [(ngModel)]="editingAppointmentNote"
        maxlength="2000"
        placeholder="Reason for visit, follow-up context, etc."
      ></textarea>
      <ng-template pTemplate="footer">
        <div class="d-flex justify-content-end gap-2 pt-2">
          <button class="btn btn-sm btn-light border px-3" (click)="appointmentNoteDialogVisible = false">
            Cancel
          </button>
          <button class="btn btn-sm btn-primary px-3" (click)="saveAppointmentNote()">
            Save note
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .calendar-shell { width: 100%; display: flex; flex-direction: column; gap: 16px; height: calc(100vh - 140px); overflow: hidden; padding-top: 0; }
    .calendar-shell.is-embedded { height: calc(100vh - 210px); padding-top: 0; }
    .calendar-toolbar {
      display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 12px 16px;
      border: 1px solid #e2e8f0; border-radius: 14px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04); flex-shrink: 0;
    }
    .calendar-context-slot {
      flex: 1 1 280px;
      min-width: 0;
      margin-left: auto;
      max-width: min(100%, 34rem);
    }
    ::ng-deep .calendar-context-slot .work-context-bar {
      margin: 0;
      width: 100%;
      min-width: 0;
      border: 1px solid #dbeafe;
      border-radius: 10px;
      background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
      box-shadow: none;
    }
    ::ng-deep .calendar-context-slot .context-shell {
      padding: 0.45rem 0.65rem;
    }
    .calendar-toolbar:has(.work-context-bar.is-editing) .calendar-context-slot {
      flex: 1 1 100%;
      max-width: 100%;
      margin-left: 0;
    }
    .date-navigator { display: flex; align-items: center; gap: 12px; }
    .nav-btn {
      border: 1px solid #e2e8f0; border-radius: 8px; width: 38px; height: 38px; display: flex; align-items: center; justify-content: center;
      background: #ffffff; color: #475569; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    .nav-btn:hover { background: #f1f5f9; color: #0f172a; border-color: #cbd5e1; }
    .date-input {
      border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 14px; font-weight: 600; font-size: 14px; color: #1e293b; background: white; cursor: pointer; outline: none; transition: border-color 0.2s;
    }
    .date-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1); }

    .clinic-columns-container {
      display: flex; gap: 16px; overflow-x: auto; overflow-y: hidden; flex: 1; padding-bottom: 12px;
      /* Scrollbar styling */
      &::-webkit-scrollbar { height: 8px; }
      &::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
      &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
      &::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    }
    .clinic-column {
      flex: 1 1 0; min-width: 300px; display: flex; flex-direction: column; background: #f8fafc; border-radius: 14px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    }
    .clinic-header {
      background: #ffffff; padding: 14px 16px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 10;
    }
    .clinic-title { margin: 0; font-size: 15px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; }
    .clinic-title::before { content: ''; display: block; width: 8px; height: 8px; border-radius: 50%; background: #3b82f6; }
    .add-btn {
      background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 4px;
    }
    .add-btn:hover { background: #dbeafe; color: #1d4ed8; }
    
    .clinic-body {
      padding: 14px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 12px;
      /* Scrollbar styling */
      &::-webkit-scrollbar { width: 6px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
    }
    .appointment-card {
      background: #ffffff; border-radius: 8px; padding: 10px; box-shadow: 0 2px 4px rgba(15,23,42,0.04); border: 1px solid #e2e8f0; border-left: 4px solid #cbd5e1; display: flex; flex-direction: column; gap: 8px; transition: transform 0.2s, box-shadow 0.2s; position: relative;
    }
    .appointment-card:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(15,23,42,0.06); border-color: #cbd5e1; }
    .card-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
    .patient-name { font-weight: 700; color: #0f172a; font-size: 13px; line-height: 1.2; }
    .appt-time { font-size: 11px; color: #64748b; font-weight: 600; display: flex; align-items: center; gap: 3px; background: #f8fafc; padding: 2px 6px; border-radius: 4px; border: 1px solid #f1f5f9; white-space: nowrap; flex-shrink: 0; }
    .card-body { display: flex; flex-direction: column; align-items: stretch; margin-top: 0; gap: 6px; }
    .card-body-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; min-width: 0; }
    .doc-info { font-size: 11px; color: #64748b; display: flex; align-items: center; gap: 4px; font-weight: 500; flex: 1; min-width: 0; }
    .card-status-pills { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
    .status-badge {
      display: inline-flex; align-items: center; font-size: 9px; padding: 3px 9px; border-radius: 999px;
      font-weight: 700; letter-spacing: 0.2px; white-space: nowrap; border: 1px solid transparent; line-height: 1.2;
    }
    
    .card-intake-note {
      display: inline-flex; align-items: center; align-self: flex-start; gap: 6px;
      padding: 4px 8px; max-width: 100%;
      border-radius: 6px; background: #ecfdf5; border: 1px solid #bbf7d0; color: #166534;
      font-size: 11px; line-height: 1.2;
    }
    .card-intake-note-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
    .card-intake-note .pi { flex-shrink: 0; font-size: 0.82rem; line-height: 1; }
    .card-intake-note .medicine-intent-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      min-width: 18px;
      padding: 0;
      border-radius: 50%;
      font-size: 10px;
      font-weight: 800;
      line-height: 1;
      flex-shrink: 0;
      overflow: visible;
    }
    .card-appointment-note {
      display: inline-flex; align-items: flex-start; align-self: flex-start; gap: 6px;
      padding: 4px 8px; max-width: 100%;
      border-radius: 6px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af;
      font-size: 11px; line-height: 1.35;
    }
    .card-appointment-note-text {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
    }
    .card-appointment-note .pi { flex-shrink: 0; font-size: 0.82rem; line-height: 1; margin-top: 1px; }
    .appointment-note-input {
      width: 100%;
      min-height: 56px;
      resize: vertical;
      font-size: 0.84rem;
      box-sizing: border-box;
    }
    .appointment-second-row__doctor-note {
      flex: 1 1 16rem;
      min-width: 0;
    }
    .intake-note-btn--doctor { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
    .medicine-intent-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 800;
      line-height: 1;
      flex-shrink: 0;
    }
    .medicine-intent-badge--continue {
      background: #ffedd5;
      color: #c2410c;
      border: 1px solid #fdba74;
    }
    .medicine-intent-badge--regular {
      background: #dbeafe;
      color: #1d4ed8;
      border: 1px solid #93c5fd;
    }
    .appointment-second-row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 12px 20px;
      margin-bottom: 0.5rem;
    }
    .appointment-second-row__delivery,
    .appointment-second-row__reception {
      flex: 0 0 auto;
      width: auto;
      max-width: 100%;
    }
    .reception-note-inline {
      display: inline-flex;
      align-items: stretch;
      gap: 4px;
      width: fit-content;
      max-width: 100%;
    }
    .reception-note-inline--dialog {
      margin-top: 2px;
    }
    .reception-note-input {
      flex: 0 0 auto;
      width: 7.25rem;
      min-width: 7.25rem;
      padding-left: 0.5rem !important;
      padding-right: 0.5rem !important;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.02em;
      box-sizing: border-box;
    }
    .appointment-dialog-compact .reception-note-input {
      width: 7.5rem;
      min-width: 7.5rem;
      font-size: 12px !important;
    }
    .reception-intent-group {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }
    .medicine-intent-badge-btn {
      flex: 0 0 auto;
      width: 32px;
      min-width: 32px;
      height: 32px;
      min-height: 32px;
      padding: 0;
      border-radius: 7px;
      border: 1px solid #dbe3ee;
      background: #fff;
      color: #64748b;
      font-size: 11px;
      font-weight: 800;
      line-height: 1;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .appointment-dialog-compact .medicine-intent-badge-btn {
      width: 32px;
      min-width: 32px;
      height: 32px;
      min-height: 32px;
      font-size: 10px;
    }
    .medicine-intent-badge-btn:hover {
      border-color: #94a3b8;
      color: #334155;
    }
    .medicine-intent-badge-btn--continue.is-active {
      background: #ffedd5;
      border-color: #fdba74;
      color: #c2410c;
    }
    .medicine-intent-badge-btn--regular.is-active {
      background: #dbeafe;
      border-color: #93c5fd;
      color: #1d4ed8;
    }

    .card-actions {
      display: flex; flex-direction: column; gap: 6px;
      border-top: 1px dashed #e2e8f0; padding-top: 6px; margin-top: 2px;
    }
    .intake-note-btn {
      width: 100%; display: flex; justify-content: center; align-items: center; gap: 4px;
      background: #fff; color: #15803d; border: 1px solid #86efac; padding: 6px; border-radius: 6px;
      font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
    }
    .intake-note-btn:hover { background: #f0fdf4; border-color: #4ade80; }
    .consult-btn {
      width: 100%; display: flex; justify-content: center; align-items: center; gap: 4px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; border: none; padding: 6px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
    }
    .consult-btn:hover { background: linear-gradient(135deg, #059669 0%, #047857 100%); transform: translateY(-1px); box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3); }

    .empty-state { text-align: center; color: #94a3b8; font-size: 13px; padding: 40px 20px; font-style: normal; font-weight: 500; background: #ffffff; border-radius: 10px; border: 1px dashed #cbd5e1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
    .empty-state::before { content: '\\e902'; font-family: 'primeicons'; font-size: 24px; color: #cbd5e1; }

    /* ===== DIALOG ===== */

    ::ng-deep .p-dialog .p-dialog-header {
      padding: 0.75rem 1rem;
    }

    ::ng-deep .p-dialog .p-dialog-content {
      overflow: hidden !important;
    }

    ::ng-deep .p-dialog .p-dialog-footer {
      padding: 0.5rem 1rem 0.75rem;
    }

    /* ===== LABELS ===== */

    .compact-label {
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
      display: block;
      margin-bottom: 3px;
    }

    .appointment-dialog-compact .compact-label {
      font-size: 11px;
      margin-bottom: 2px;
    }

    .compact-textarea {
      font-size: 14px;
      border-radius: 8px;
      border: 1px solid #dbe3ee;
      resize: vertical;
      min-height: 64px;
    }

    .intake-note-dialog-hint {
      font-size: 13px;
      color: #64748b;
      margin: 0;
    }

    /* ===== INPUTS ===== */

    ::ng-deep .compact-input .p-inputtext,
    ::ng-deep .compact-input .p-dropdown,
    .compact-date-input,
    .compact-reception-input {
      height: 36px !important;
      min-height: 36px !important;
      font-size: 14px !important;
      border-radius: 8px !important;
    }

    .appointment-dialog-compact ::ng-deep .compact-input .p-inputtext,
    .appointment-dialog-compact ::ng-deep .compact-input .p-dropdown,
    .appointment-dialog-compact .compact-date-input,
    .appointment-dialog-compact .compact-reception-input {
      height: 32px !important;
      min-height: 32px !important;
      font-size: 12px !important;
      border-radius: 7px !important;
    }

    ::ng-deep .compact-input .p-dropdown-label {
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
      font-size: 14px !important;
    }

    .appointment-dialog-compact ::ng-deep .compact-input .p-dropdown-label {
      padding-top: 0.35rem !important;
      padding-bottom: 0.35rem !important;
      font-size: 12px !important;
    }

    ::ng-deep .compact-input .p-inputtext {
      padding: 0.5rem 0.75rem !important;
    }

    .appointment-dialog-compact ::ng-deep .compact-input .p-inputtext {
      padding: 0.35rem 0.55rem !important;
    }

    .compact-reception-input {
      padding: 0.35rem 0.55rem !important;
      border: 1px solid #dbe3ee;
      background: #f8fafc;
    }

    .appointment-second-row .appointment-flow-badge-group {
      min-height: 32px;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 0.4rem;
    }

    .appointment-second-row .appointment-flow-badge {
      padding: 0.28rem 0.58rem;
      font-size: 0.72rem;
    }

    /* ===== DATE INPUT ===== */

    .compact-date-wrapper {
      position: relative;
    }

    .date-icon {
      position: absolute;
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      color: #3b82f6;
      font-size: 12px;
    }

    .compact-date-input {
      padding-left: 28px !important;
      background: #f8fafc;
    }

    .appointment-dialog-compact .date-icon {
      font-size: 11px;
      left: 7px;
    }

    .appointment-dialog-compact .compact-date-input {
      padding-left: 24px !important;
    }

    /* ===== DELIVERY BADGES ===== */

    .appointment-flow-badge-group {
      display: flex;
      flex-wrap: wrap;
      gap: 0.45rem;
      min-height: 36px;
      align-items: center;
    }

    .appointment-flow-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.38rem 0.72rem;
      border-radius: 999px;
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      color: #475569;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      cursor: pointer;
      transition: all 0.18s ease;
    }

    .appointment-flow-badge:hover {
      border-color: #94a3b8;
      background: #f1f5f9;
    }

    .appointment-flow-badge--medicine.is-active {
      background: rgba(16, 185, 129, 0.14);
      border-color: rgba(16, 185, 129, 0.45);
      color: #047857;
      box-shadow: inset 0 0 0 1px rgba(16, 185, 129, 0.12);
    }

    .appointment-flow-badge--home_delivery.is-active {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      border-color: transparent;
      color: #ffffff;
      box-shadow: 0 3px 10px rgba(37, 99, 235, 0.24);
    }

    .appointment-flow-badge--domestic.is-active {
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      border-color: transparent;
      color: #ffffff;
      box-shadow: 0 3px 10px rgba(124, 58, 237, 0.24);
    }

    .appointment-flow-badge--international.is-active {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      border-color: transparent;
      color: #ffffff;
      box-shadow: 0 3px 10px rgba(217, 119, 6, 0.24);
    }

    /* ===== TIME SLOTS ===== */

    .time-slot-container {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 0.75rem;
      background: white;
    }

    .slot-empty-note {
      padding: 0.75rem;
      border-radius: 8px;
      background: #f8fafc;
      color: #64748b;
      font-size: 0.9rem;
      text-align: center;
    }

    .slot-btn {
      height: 32px;
      border: 1px solid #dbeafe;
      background: white;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
      transition: all 0.2s ease;
    }

    .slot-btn:hover:not(:disabled) {
      border-color: #3b82f6;
      background: #eff6ff;
      box-shadow: 0 4px 6px rgba(59, 130, 246, 0.15);
      transform: translateY(-1px);
    }

    .slot-btn.selected {
      background: #2563eb;
      color: white;
      border-color: #2563eb;
      box-shadow: 0 4px 8px rgba(37, 99, 235, 0.3);
      transform: translateY(-1px);
    }

    .slot-btn:disabled {
      background: #e2e8f0;
      color: #64748b;
      border-color: #cbd5e1;
      opacity: 0.7;
      cursor: not-allowed;
      box-shadow: none;
    }

    /* ===== MOBILE ===== */

    @media (max-width: 768px) {

      .compact-toggle-wrapper {
        margin-top: 0.5rem;
        justify-content: flex-start;
      }

      .slot-btn {
        font-size: 12px;
      }

    }
  `]
})
export class CalendarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() showContextBar = true;

  public appointment: any = {
    id: 0,
    clinic_id: 0,
    patient_id: 0,
    doctor_id: 0,
    appointment_type: '',
    appointment_date: todayDateString(),
    start_time: '',
    end_time: '',
    notes: '',
    appointment_note: '',
    medicine_intent: null as MedicineIntent | null,
    medicine_collection: false,
    delivery_type: null,
    is_courier: false
  }
  readonly deliveryTypeOptions = DELIVERY_TYPE_OPTIONS;
  readonly medicineIntentOptions = MEDICINE_INTENT_OPTIONS;
  userType: string = '';

  currentDateString: string = todayDateString();
  groupedAppointments: { [clinicId: number]: any[] } = {};

  patientDialogVisible = false;
  intakeNoteDialogVisible = false;
  appointmentNoteDialogVisible = false;
  editingIntakeAppointment: any = null;
  editingIntakeNote = '';
  editingIntakeMedicineIntent: MedicineIntent | null = null;
  editingAppointmentNoteTarget: any = null;
  editingAppointmentNote = '';
  selectedPatient: any;
  patients: any[] = [];
  public clinicList: any[] = [];
  public doctorsList: any[] = [];

  timeSlots: any[] = [];
  selectedSlot: any;

  private apiUrl: string | undefined;
  private intervalId: any;

  constructor(
    private appointmentService: AppointmentService,
    private http: HttpClient,
    private toaster: ToasterService, private cdr: ChangeDetectorRef,
    private clinicService: ClinicService,
    private clinicScheduleService: ClinicScheduleService,
    private userService: UserService,
    private patientRegistrationService: PatientRegistrationService,
    private apiService: ApiService,
    private router: Router,
    private workContext: WorkContextService
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  ngOnInit() {
    const storedClinic = sessionStorage.getItem('clinic_id');
    const storedDoctor = sessionStorage.getItem('doctor_id');
    const loggedInUser = JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
    this.userType = loggedInUser.type || '';

    if (storedClinic) this.appointment.clinic_id = +storedClinic;
    if (storedDoctor) this.appointment.doctor_id = +storedDoctor;

    this.getClinic();
    this.getDoctors();
    this.clinicScheduleService.ensureClinicsLoaded().subscribe(() => this.generateTimeSlots());

    this.loadEvents();

    this.intervalId = setInterval(() => {
      this.generateTimeSlots();
      this.loadEvents();
    }, 60 * 1000);
  }

  ngAfterViewInit() { }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  previousDay() {
    this.currentDateString = shiftDateString(this.currentDateString, -1);
    this.loadEvents();
  }

  nextDay() {
    this.currentDateString = shiftDateString(this.currentDateString, 1);
    this.loadEvents();
  }

  onDateChange() {
    this.loadEvents();
  }

  loadEvents(): void {
    const dateStr = this.currentDateString;

    this.appointmentService.getAppointment(dateStr, dateStr).subscribe({
      next: (result: any) => {
        let appointments = Array.isArray(result.data) ? result.data : [];

        // Sort by time
        appointments.sort((a: any, b: any) => a.start_time.localeCompare(b.start_time));

        // Group by clinic
        this.groupedAppointments = {};
        for (let appt of appointments) {
          if (!this.groupedAppointments[appt.clinic_id]) {
            this.groupedAppointments[appt.clinic_id] = [];
          }
          this.groupedAppointments[appt.clinic_id].push(appt);
        }
      },
      error: (err) => {
        console.error("Error loading appointments:", err);
        this.groupedAppointments = {};
      }
    });
  }

  openAddAppointment(clinicId: number) {
    this.appointment.clinic_id = clinicId;
    this.appointment.appointment_date = this.currentDateString;
    this.appointment.notes = '';
    this.appointment.appointment_note = '';
    this.appointment.medicine_intent = null;
    this.appointment.medicine_collection = false;
    this.appointment.delivery_type = null;
    this.appointment.is_courier = false;
    this.generateTimeSlots();
    this.selectedPatient = null;
    this.selectedSlot = null;
    this.patientDialogVisible = true;
  }

  goToConsultation(appt: any) {
    this.workContext.ensureOptionsLoaded().subscribe(() => {
      this.workContext.setContextForConsultation(appt.clinic_id, appt.doctor_id);
      this.router.navigate(['/admin/prescriptions']);
    });
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    };
  }

  getClinic() {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data) ? res.data.map((c: any) => ({ label: c.name, value: c.id })) : [];
      },
      error: (err) => { console.error('Clinic API error:', err); this.clinicList = []; }
    });
  }

  getDoctors() {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data) ? res.data.map((d: any) => ({ label: d.name, value: d.id })) : [];
      },
      error: (err) => { console.error('Doctor API error:', err); this.doctorsList = []; }
    });
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders() })
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patients = [];
          this.toaster.add({ severity: 'info', summary: 'No patients found', detail: `No patients found for "${query}".` });
        } else {
          this.patients = res.data.map((p: any) => ({ ...p, full_name: `${p.full_name} (${p.patient_number})` }));
        }
        this.cdr.detectChanges();
      }, error => {
        this.patients = [];
        this.toaster.info('No patients found.', 'Info');
        this.cdr.detectChanges();
      });
  }

  confirmPatientSelection() {
    if (!this.selectedPatient || !this.selectedSlot) return;

    const appointmentData = {
      patient_id: this.selectedPatient.id || this.selectedPatient.patient_id,
      clinic_id: this.appointment.clinic_id,
      doctor_id: this.appointment.doctor_id,
      appointment_type: this.appointment.appointment_type,
      appointment_date: this.appointment.appointment_date,
      start_time: this.appointment.start_time,
      end_time: this.appointment.end_time,
      status: 'scheduled',
      notes: (this.appointment.notes || '').trim() || null,
      appointment_note: (this.appointment.appointment_note || '').trim() || null,
      medicine_intent: this.appointment.medicine_intent,
      medicine_collection: this.appointment.medicine_collection,
      delivery_type: this.appointment.delivery_type,
      is_courier: !!this.appointment.delivery_type,
    };

    this.appointmentService.addAppointment(appointmentData).subscribe({
      next: (created) => {
        this.patientDialogVisible = false;
        this.selectedPatient = null;
        this.selectedSlot = null;
        this.appointment.notes = '';
        this.appointment.appointment_note = '';
        this.appointment.medicine_intent = null;
        this.toaster.success('Appointment created successfully.');
        this.loadEvents();
      },
      error: (err) => {
        let detailMsg = 'Failed to create appointment.';
        if (err.status === 422 && err.error && err.error.errors && err.error.errors.start_time) {
          detailMsg = err.error.errors.start_time[0];
        }
        this.toaster.error(detailMsg);
      }
    });
  }

  openIntakeNoteEditor(appt: any): void {
    this.editingIntakeAppointment = appt;
    this.editingIntakeNote = this.sanitizeReceptionNote(appt?.notes || '');
    this.editingIntakeMedicineIntent = appt?.medicine_intent ?? null;
    this.intakeNoteDialogVisible = true;
  }

  getMedicineIntentShort(value: MedicineIntent | null | undefined): string {
    return medicineIntentShortLabel(value);
  }

  getReceptionNoteTooltip(appt: any): string {
    return receptionNoteTooltip(appt?.notes, appt?.medicine_intent);
  }

  isMedicineIntentActive(type: MedicineIntent, target: 'appointment' | 'edit'): boolean {
    const current = target === 'appointment'
      ? this.appointment.medicine_intent
      : this.editingIntakeMedicineIntent;
    return current === type;
  }

  setMedicineIntent(type: MedicineIntent, target: 'appointment' | 'edit'): void {
    const current = target === 'appointment'
      ? this.appointment.medicine_intent
      : this.editingIntakeMedicineIntent;
    const next: MedicineIntent | null = current === type ? null : type;

    if (target === 'appointment') {
      this.appointment.medicine_intent = next;
      return;
    }

    this.editingIntakeMedicineIntent = next;
  }

  sanitizeReceptionNote(value: string): string {
    return (value || '').replace(/\D/g, '').slice(0, 10);
  }

  onReceptionNoteChange(value: string, target: 'appointment' | 'edit'): void {
    const digits = this.sanitizeReceptionNote(value);
    if (target === 'appointment') {
      this.appointment.notes = digits;
      if (!digits) {
        this.appointment.medicine_intent = null;
      }
      return;
    }
    this.editingIntakeNote = digits;
    if (!digits) {
      this.editingIntakeMedicineIntent = null;
    }
  }

  saveIntakeNote(): void {
    const appt = this.editingIntakeAppointment;
    if (!appt?.id) {
      return;
    }

    const notes = (this.editingIntakeNote || '').trim();
    this.appointmentService.updateAppointment({
      id: appt.id,
      notes: notes || null,
      medicine_intent: notes ? this.editingIntakeMedicineIntent : null,
    }).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.toaster.error(res?.message || 'Failed to save reception note.');
          return;
        }

        appt.notes = notes;
        appt.medicine_intent = notes ? this.editingIntakeMedicineIntent : null;
        this.intakeNoteDialogVisible = false;
        this.editingIntakeAppointment = null;
        this.editingIntakeNote = '';
        this.editingIntakeMedicineIntent = null;
        this.toaster.success('Reception note saved.');
      },
      error: () => this.toaster.error('Failed to save reception note.'),
    });
  }

  openAppointmentNoteEditor(appt: any): void {
    this.editingAppointmentNoteTarget = appt;
    this.editingAppointmentNote = String(appt?.appointment_note || '').trim();
    this.appointmentNoteDialogVisible = true;
  }

  saveAppointmentNote(): void {
    const appt = this.editingAppointmentNoteTarget;
    if (!appt?.id) {
      return;
    }

    const appointmentNote = (this.editingAppointmentNote || '').trim();
    this.appointmentService.updateAppointment({
      id: appt.id,
      appointment_note: appointmentNote || null,
    }).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.toaster.error(res?.message || 'Failed to save appointment note.');
          return;
        }

        appt.appointment_note = appointmentNote;
        this.appointmentNoteDialogVisible = false;
        this.editingAppointmentNoteTarget = null;
        this.editingAppointmentNote = '';
        this.toaster.success('Appointment note saved.');
      },
      error: () => this.toaster.error('Failed to save appointment note.'),
    });
  }

  onPatientSelect(event: any) {
    this.appointment.patient_id = event.value.id || event.value.patient_id;
    this.selectedPatient = event.value;
  }

  setMedicineCollection(active: boolean) {
    this.appointment.medicine_collection = !!active;
    if (this.appointment.medicine_collection) {
      this.appointment.delivery_type = null;
      this.appointment.is_courier = false;
    }
  }

  isDeliveryTypeActive(type: DeliveryType): boolean {
    return resolveDeliveryType(this.appointment) === type;
  }

  setDeliveryType(type: DeliveryType) {
    const current = resolveDeliveryType(this.appointment);
    const next: DeliveryType | null = current === type ? null : type;
    this.appointment.delivery_type = next;
    this.appointment.is_courier = !!next;
    if (next) {
      this.appointment.medicine_collection = false;
    }
  }

  getAppointmentStatus(appt: any): string {
    return appt?.status || 'scheduled';
  }

  formatStatusLabel(status: string): string {
    return (status || 'scheduled')
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /** Flow type pill (medicine pickup / delivery) — separate from visit status. */
  getFlowLabel(appt: any): string | null {
    if (appt?.medicine_collection) {
      return 'Medicine';
    }
    const delivery = resolveDeliveryType(appt);
    if (delivery) {
      return deliveryTypeShortLabel(delivery);
    }
    return null;
  }

  getAppointmentStatusBadges(appt: any): Array<{ label: string; color: string; bg: string; border: string }> {
    const badges: Array<{ label: string; color: string; bg: string; border: string }> = [];
    const status = this.getAppointmentStatus(appt);
    const statusLabel = this.formatStatusLabel(status);

    badges.push({
      label: statusLabel,
      color: this.getStatusColor(status),
      bg: this.getStatusBgColor(status),
      border: this.getStatusBorderColor(status),
    });

    const flowLabel = this.getFlowLabel(appt);
    if (flowLabel && flowLabel.toLowerCase() !== statusLabel.toLowerCase()) {
      badges.push({
        label: flowLabel,
        color: '#5b21b6',
        bg: '#ede9fe',
        border: '#ddd6fe',
      });
    }

    return badges;
  }

  /** @deprecated Use getAppointmentStatus — kept for any external references */
  getDisplayStatus(appt: any): string {
    return this.getAppointmentStatus(appt);
  }

  getStatusColor(status: string) {
    switch (status) {
      case 'completed': return '#166534';
      case 'cancelled': return '#991b1b';
      case 'scheduled': return '#3b82f6';
      case 'checked in':
      case 'in progress': return '#d97706';
      case 'medicine': return '#6d28d9';
      default: return '#64748b';
    }
  }

  getStatusBgColor(status: string) {
    switch (status) {
      case 'completed': return '#dcfce7';
      case 'cancelled': return '#fee2e2';
      case 'scheduled': return '#eff6ff';
      case 'checked in':
      case 'in progress': return '#fef3c7';
      case 'medicine': return '#f3e8ff';
      default: return '#f8fafc';
    }
  }

  getStatusBorderColor(status: string) {
    switch (status) {
      case 'completed': return '#86efac';
      case 'cancelled': return '#fecaca';
      case 'scheduled': return '#bfdbfe';
      case 'checked in':
      case 'in progress': return '#fcd34d';
      case 'medicine': return '#ddd6fe';
      default: return '#e2e8f0';
    }
  }

  onClinicChange() {
    this.selectedSlot = null;
    this.generateTimeSlots();
  }

  generateTimeSlots() {
    const clinicId = this.appointment?.clinic_id;
    const date = this.appointment?.appointment_date || this.currentDateString;
    this.timeSlots = this.clinicScheduleService.generateTimeSlots(clinicId, date);
  }

  formatTime24(hour: number): string {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  formatTime(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    let h = parseInt(hours, 10);
    const suffix = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${minutes} ${suffix}`;
  }

  formatAmPm(hour: number): string {
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = ((hour + 11) % 12 + 1);
    return `${displayHour}:00 ${suffix}`;
  }

  selectSlot(slot: any) {
    if (slot.disabled) return;
    this.selectedSlot = slot;
    this.appointment.start_time = slot.start;
    this.appointment.end_time = slot.end;

    this.toaster.add({
      severity: 'info',
      summary: 'Slot Selected',
      detail: `Time slot ${slot.label} selected.`,
      life: 2000
    });
  }

  // goToConsultation(appt: any) {
  //   if (appt.clinic_id) {
  //     sessionStorage.setItem('clinic_id', appt.clinic_id.toString());
  //   }
  //   if (appt.doctor_id) {
  //     sessionStorage.setItem('doctor_id', appt.doctor_id.toString());
  //   }
  //   this.router.navigate(['/admin/prescriptions']);
  // }
}
